import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { FormattedMessage } from '../../../util/reactIntl';
import { Tooltip, IconSpinner, InquiryModal, Button, NamedLink } from '../../../components';
import { ConditionalWrapper } from '../../../util/common';

import { navigateToLatestTx } from '../helpers';
import config from '../../../config';
import { ensureCurrentUser } from '../../../util/data';

import css from './AcceptInquirySection.css';
import { useDispatch, useSelector } from 'react-redux';
import AcceptInquiryButton from './AcceptInquiryButton';
import LastActivityCaption from './LastActivityCaption';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routeConfiguration';
import { useLatestTransactionListingId } from '../useLatestTransactionListingId';
import { sendEnquiry } from '../../../containers/ListingPage/ListingPage.duck';
import {
    TRANSACTION_STATES_STATUS_ACCEPTED,
    TRANSACTION_STATES_STATUS_DECLINED,
    TRANSACTION_STATES_STATUS_ENQUIRED,
} from '../../../util/transaction';
import { ERROR_ACTIVE_TX_ALREADY_EXISTS } from '../../../components/InquiryModal/InquiryModal.helpers';
import { RIDER_AVAILABILITY_NOT_AVAILABLE } from '../../../marketplace-custom-config';
import { setEntryDataToRedirectAfterAuth } from '../../../util/localStorage';
import { trimDisplayNameLastWord } from '../../../util/text';
import { getListingNavParams } from '../../../util/listings';

const { userTypeRider, userTypeHorseowner } = config;

export default ({
    user,
    userName,
    rootClassName,
    disabled,
    isSameUser,
    riderPage,
    isLoggedOut,
    riderListingId,
    transactionDataInProgress,
}) => {
    const [inquiryModalVisible, setInquiryModalVisibility] = useState(false);

    const history = useHistory();
    const dispatch = useDispatch();

    /** Redux store data */
    const currentUser = useSelector(s => s.user.currentUser);
    const sendEnquiryError = useSelector(s => s.ListingPage.sendEnquiryError);
    const sendEnquiryInProgress = useSelector(s => s.ListingPage.sendEnquiryInProgress);
    const ownListings = useSelector(s => {
        try {
            return Object.values(s.ManageListingsPage.ownEntities?.ownListing || {});
        } catch (e) {
            return [];
        }
    });

    const onSendEnquiry = values => dispatch(sendEnquiry(values));

    const {
        id: { uuid: publicUserId },
        attributes: {
            profile: {
                displayName,
                publicData: { userType: publicUserType, availabilityStatus },
            },
        },
    } = user;

    const {
        id,
        attributes: {
            profile: {
                publicData: { userType: currentUserType },
            },
        },
    } = ensureCurrentUser(currentUser);

    const { uuid: currentUserId } = id || {};

    const isRider = currentUserType === userTypeRider;
    const isHorseowner = currentUserType === userTypeHorseowner;

    const riderIsNotAvailable =
        riderPage && availabilityStatus === RIDER_AVAILABILITY_NOT_AVAILABLE;

    const ridersViews = isRider && publicUserType === userTypeRider;
    const ownersViews = isHorseowner && publicUserType === userTypeHorseowner;

    const noListingsForHorseowner =
        isHorseowner && !ownersViews && ((ownListings && ownListings.length === 0) || !ownListings);
    const isHorseownerWithListings =
        isHorseowner && !ownersViews && ownListings && ownListings.length > 0;

    const noPublishedListingsForHorseowner =
        isHorseownerWithListings && ownListings.every(l => l.attributes.state !== 'published');

    const allHorseownerListingAreDrafts =
        isHorseownerWithListings && ownListings.every(l => l.attributes.state === 'draft');

    const allHorseownerListingArePending =
        isHorseownerWithListings &&
        ownListings.every(l => l.attributes.state === 'pendingApproval');

    const inquiryNotAllowed =
        riderIsNotAvailable ||
        (isLoggedOut
            ? false
            : (disabled && !isSameUser) ||
              noPublishedListingsForHorseowner ||
              ridersViews ||
              ownersViews);

    const actionDisabed =
        riderIsNotAvailable ||
        (isLoggedOut
            ? false
            : disabled ||
              isSameUser ||
              noPublishedListingsForHorseowner ||
              ridersViews ||
              ownersViews);

    const resolveInquiryNotAllowedKey = () => {
        const horseownerAvailableListingExists = Boolean(ownListings[0]);
        const listingNavParams =
            horseownerAvailableListingExists && getListingNavParams(ownListings[0]);
        const horseownerListingLink = listingNavParams && (
            <NamedLink name={listingNavParams.name} params={listingNavParams?.params}>
                Profil von {ownListings[0]?.attributes?.title}
            </NamedLink>
        );

        if (riderIsNotAvailable) {
            return {
                key: 'inquiryToRiderIsNotAllowedForHorseowner',
            };
        }
        if (isSameUser) {
            return { key: 'isSameUser' };
        }
        if (allHorseownerListingArePending && horseownerAvailableListingExists) {
            return {
                key: 'allHorseownerListingArePending',
                params: {
                    listingLink: horseownerListingLink,
                },
            };
        }

        if (allHorseownerListingAreDrafts && horseownerAvailableListingExists) {
            return {
                key: 'allHorseownerListingAreDrafts',
                params: { listingLink: horseownerListingLink },
            };
        }
        if (!ridersViews && noPublishedListingsForHorseowner) {
            return { key: 'noPublishedListingsForHorseowner' };
        }
        if (inquiryNotAllowed) {
            return { key: 'inquiryNotAllowedForRider' };
        }

        return {};
    };

    const {
        key: inquiryNotAllowedKey,
        params: inquiryNotAllowedListingParams = { listingLink: '' },
    } = resolveInquiryNotAllowedKey();
    const { listingLink: inquiryNotAllowedListingLink } = inquiryNotAllowedListingParams;

    const latestTransactionId = useLatestTransactionListingId(user?.id?.uuid);

    const loading = /* horseownerListingsInProgress || */ transactionDataInProgress;

    const onInquiryProcessInitiating = async transactionsState => {
        if (isLoggedOut) {
            return history.push(
                createResourceLocatorString(
                    'SignupHorseowner',
                    routeConfiguration(),
                    {},
                    riderListingId ? { listingId: riderListingId } : {}
                )
            );
        }
        const isEnquired = transactionsState === TRANSACTION_STATES_STATUS_ENQUIRED;
        const isDeclinedStatus = transactionsState === TRANSACTION_STATES_STATUS_DECLINED;
        const isAcceptedStatus = transactionsState === TRANSACTION_STATES_STATUS_ACCEPTED;

        if (isAcceptedStatus || isDeclinedStatus || isEnquired) {
            /**
             * ERROR_ACTIVE_TX_ALREADY_EXISTS is set artificially,
             * just to pass the call check for code param
             */
            return navigateToLatestTx(ERROR_ACTIVE_TX_ALREADY_EXISTS, history, latestTransactionId);
        }

        setInquiryModalVisibility(true);
    };

    const handleNoListingsRequest = () => {
        /**
         * If an owner does not have a listing yet and wants to inquire a rider:
         * -pre-save this rider data;
         * -navigate the owner to Upload a new horse page;
         * -approve/publish a listing;
         * -navigate the owner back to this very rider once a new horse created;
         */

        /** -pre-save this rider data */
        setEntryDataToRedirectAfterAuth({
            name: 'ProfilePage',
            params: {
                id: publicUserId,
                userType: userTypeRider,
            },
            redirectAfterAuthInquiryData: {
                riderName: trimDisplayNameLastWord(displayName || 'Reiter'),
            },
        });
        /** -navigate the owner to Upload a new horse page */
        history.push(createResourceLocatorString('NewListingPage', routeConfiguration()));
    };

    return (
        <>
            {inquiryModalVisible && (
                <InquiryModal
                    isOpen
                    onClose={() => setInquiryModalVisibility(false)}
                    publicUser={user}
                    onSendEnquiry={onSendEnquiry}
                    sendEnquiryError={sendEnquiryError}
                    sendEnquiryInProgress={sendEnquiryInProgress}
                    ridingListingAuthorId={isRider ? currentUserId : publicUserId}
                    notifyOnError={code => navigateToLatestTx(code, history, latestTransactionId)}
                />
            )}
            {loading ? (
                <IconSpinner />
            ) : (
                <aside className={rootClassName || css.acceptInquirySection}>
                    <LastActivityCaption
                        currentUser={currentUser}
                        publicUser={user}
                        isSameUser={isSameUser}
                    />
                    <ConditionalWrapper
                        condition={inquiryNotAllowed && inquiryNotAllowedKey}
                        wrapper={children => (
                            <Tooltip
                                position="top"
                                tooltipClassName={css.inquiryDisabledTooltip}
                                content={
                                    <div>
                                        <p>Anfragen nicht möglich</p>
                                        <p>
                                            <FormattedMessage
                                                id={`Inquiry.action.disabled.desc.${inquiryNotAllowedKey}`}
                                                values={{
                                                    userName,
                                                    listingLink: inquiryNotAllowedListingLink,
                                                }}
                                            />
                                        </p>
                                    </div>
                                }
                            >
                                {children}
                            </Tooltip>
                        )}
                    >
                        {noListingsForHorseowner ? (
                            <Button type="button" onClick={handleNoListingsRequest}>
                                Anfrage senden
                            </Button>
                        ) : (
                            <AcceptInquiryButton
                                onInquiryProcessInitiating={onInquiryProcessInitiating}
                                disabled={actionDisabed}
                                publicUser={user}
                            />
                        )}
                    </ConditionalWrapper>
                </aside>
            )}
        </>
    );
};
